import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Add } from '@mui/icons-material'
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InternalPackage } from '../../../plans/types/Plans'
import { AddedOrganizationPackage, InternalOrganization } from '../../types/Organizations'
import { AddPackageDialog } from '../AddPackageDialog/AddPackageDialog'
import { OrganizationPackagesTable } from '../OrganizationPackagesTable/OrganizationPackagesTable'
import {
  billingTypes,
  EditableTextField,
  FormPackage,
  getWholeNumberError,
  invoiceCurrencies,
  isOrganizationFormChanged,
  invoiceFrequencies,
  organizationLanguages,
  OrganizationFormValues,
  paymentTerms,
  toOrganizationFormValues,
  withStoredOption,
} from '../helpers/organizationHelpers'

type OrganizationEditDialogProps = {
  open: boolean
  organization?: InternalOrganization
  catalog: InternalPackage[]
  isSaving?: boolean
  onClose: () => void
  onSave: (values: OrganizationFormValues) => void
}

type TextInput = { name: EditableTextField; labelKey: string; placeholder?: string }
type SelectInput = { name: EditableTextField; labelKey: string; options: string[] }
type ContactInput = { name: 'name' | 'email' | 'phone'; labelKey: string; placeholder: string }

const billingSelects: SelectInput[] = [
  { name: 'billingType', labelKey: 'internal-csm:organizations_invoice_type', options: billingTypes },
  { name: 'invoiceCurrency', labelKey: 'internal-csm:organizations_invoice_currency', options: invoiceCurrencies },
  { name: 'invoiceFrequency', labelKey: 'internal-csm:organizations_invoice_frequency', options: invoiceFrequencies },
  { name: 'paymentTerms', labelKey: 'internal-csm:organizations_payment_terms', options: paymentTerms },
]

const billingAddress: TextInput[] = [
  { name: 'billingAddress1', labelKey: 'internal-csm:organizations_address_line_1' },
  { name: 'billingAddress2', labelKey: 'internal-csm:organizations_address_line_2' },
  { name: 'billingZipCode', labelKey: 'internal-csm:organizations_zip_code' },
  { name: 'billingCity', labelKey: 'internal-csm:organizations_city' },
  { name: 'billingCountry', labelKey: 'internal-csm:organizations_country' },
]

const companyAddress: TextInput[] = [
  { name: 'address1', labelKey: 'internal-csm:organizations_address_line_1' },
  { name: 'address2', labelKey: 'internal-csm:organizations_address_line_2' },
  { name: 'zipCode', labelKey: 'internal-csm:organizations_zip_code' },
  { name: 'city', labelKey: 'internal-csm:organizations_city' },
  { name: 'country', labelKey: 'internal-csm:organizations_country' },
]

const companyContact: TextInput[] = [
  { name: 'contactName', labelKey: 'internal-csm:organizations_contact_name', placeholder: 'Jane Doe' },
  { name: 'contactEmail', labelKey: 'internal-csm:organizations_contact_email', placeholder: 'jane.doe@example.com' },
  { name: 'contactPhone', labelKey: 'internal-csm:organizations_contact_phone', placeholder: '+358 123456789' },
]

const billingContact: ContactInput[] = [
  { name: 'name', labelKey: 'internal-csm:organizations_contact_name', placeholder: 'Jane Doe' },
  { name: 'email', labelKey: 'internal-csm:organizations_contact_email', placeholder: 'jane.doe@example.com' },
  { name: 'phone', labelKey: 'internal-csm:organizations_contact_phone', placeholder: '+358 123456789' },
]

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <Stack spacing={2}>
    <Typography variant="subtitle1" fontWeight="bold">
      {title}
    </Typography>
    {children}
  </Stack>
)

export const OrganizationEditDialog: FC<OrganizationEditDialogProps> = ({ open, organization, catalog, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, getValues, setValue, watch } = useForm<OrganizationFormValues>({
    mode: 'onChange',
    defaultValues: toOrganizationFormValues(organization),
  })
  const [addPackageOpen, setAddPackageOpen] = useState(false)
  const [removeConfirm, setRemoveConfirm] = useState<ConfirmDialogData<FormPackage>>()

  useEffect(() => {
    if (open) reset(toOrganizationFormValues(organization))
  }, [open, organization, reset])

  const packages = watch('packages')
  const name = watch('name')
  const displayName = organization?.name || name
  const addedIds = useMemo(() => packages.map(({ packageId }) => packageId), [packages])
  const sortedCatalog = useMemo(() => [...catalog].sort((a, b) => a.name.localeCompare(b.name)), [catalog])

  const askToRemove = useCallback(
    (planPackage: FormPackage) =>
      setRemoveConfirm({
        title: t('internal-csm:organizations_remove_package'),
        confirmText: t('internal-csm:organizations_remove_package_confirm', { name: planPackage.name, organization: displayName }),
        actionText: t('internal-csm:organizations_remove'),
        destructiveAction: true,
        data: planPackage,
      }),
    [t, displayName]
  )

  const handleRemoveConfirm = (confirmed?: ConfirmDialogData<FormPackage>) => {
    setRemoveConfirm(undefined)
    const removed = confirmed?.data
    if (!removed) return
    const current = getValues('packages')
    const index = current.findIndex(({ packageId, purchasedAt }) => packageId === removed.packageId && purchasedAt === removed.purchasedAt)
    if (index >= 0) setValue('packages', [...current.slice(0, index), ...current.slice(index + 1)], { shouldDirty: true, shouldValidate: true })
  }

  const handleAdd = (added: AddedOrganizationPackage[]) => {
    setAddPackageOpen(false)
    setValue('packages', [...getValues('packages'), ...added], { shouldDirty: true, shouldValidate: true })
  }

  const renderText = ({ name: field, labelKey, placeholder }: TextInput) => (
    <Controller
      key={field}
      name={field}
      control={control}
      render={({ field: input }) => <TextField {...input} size="small" fullWidth label={t(labelKey)} placeholder={placeholder} disabled={isSaving} />}
    />
  )

  const maxFollowedGames = organization?.maxFollowedGames ?? 0
  const submit = handleSubmit(onSave)
  const values = watch()
  const initialValues = useMemo(() => toOrganizationFormValues(organization), [organization])
  // formState.isValid and isDirty can lag behind quick successive changes, so both are derived from the values.
  const isValid =
    !!values.name.trim() &&
    !!values.defaultLanguage &&
    !getWholeNumberError(values.followedGamesMonthCount, maxFollowedGames) &&
    !getWholeNumberError(values.maxSeats)
  const canSave = isValid && isOrganizationFormChanged(values, initialValues) && !isSaving

  return (
    <InternalFormDialog
      open={open}
      title={organization ? t('internal-csm:organizations_edit_title', { name: organization.name }) : t('internal-csm:organizations_new_title')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!canSave}
      isSaving={isSaving}
      maxWidth="lg"
    >
      <Stack spacing={3} pt={1}>
        <Stack direction="row" spacing={2}>
          <Controller
            name="name"
            control={control}
            rules={{ validate: (value) => (value.trim() ? true : t('internal-csm:organizations_name_required')) }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                fullWidth
                required
                label={t('internal-csm:organizations_company_name')}
                placeholder={t('internal-csm:organizations_company_name_placeholder')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={isSaving}
              />
            )}
          />
          <Controller
            name="defaultLanguage"
            control={control}
            rules={{ validate: (value) => (value ? true : t('internal-csm:organizations_language_required')) }}
            render={({ field, fieldState }) => {
              // Most stored organizations have no default language; say up front that it blocks saving.
              const missing = !!organization && !field.value
              return (
                <TextField
                  {...field}
                  select
                  size="small"
                  fullWidth
                  required
                  label={t('internal-csm:organizations_default_language')}
                  error={!!fieldState.error || missing}
                  helperText={fieldState.error?.message ?? (missing ? t('internal-csm:organizations_language_required') : undefined)}
                  disabled={isSaving}
                >
                  {organizationLanguages.map((language) => (
                    <MenuItem key={language} value={language}>
                      {t(`internal-common:${language}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )
            }}
          />
        </Stack>

        <Section title={t('internal-csm:organizations_plans')}>
          <Box>
            <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => setAddPackageOpen(true)} disabled={isSaving}>
              {t('internal-csm:organizations_add_package')}
            </Button>
          </Box>
          <OrganizationPackagesTable packages={packages} onRemove={askToRemove} disabled={isSaving} />
        </Section>

        <Section title={t('internal-csm:organizations_usage_limits')}>
          <Controller
            name="trialUsed"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={isSaving} />}
                label={t('internal-csm:organizations_trial_used')}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <Controller
              name="followedGamesMonthCount"
              control={control}
              rules={{
                validate: (value) => {
                  const error = getWholeNumberError(value, maxFollowedGames)
                  return error ? t(`internal-csm:organizations_number_${error}`, { max: maxFollowedGames }) : true
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  required
                  label={
                    organization && maxFollowedGames > 0
                      ? t('internal-csm:organizations_followed_games_month_count_max', { max: maxFollowedGames })
                      : t('internal-csm:organizations_followed_games_month_count')
                  }
                  inputProps={{ inputMode: 'numeric' }}
                  disabled={isSaving}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="maxSeats"
              control={control}
              rules={{
                validate: (value) => {
                  const error = getWholeNumberError(value)
                  return error ? t(`internal-csm:organizations_number_${error}`) : true
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  required
                  label={t('internal-csm:organizations_max_seats')}
                  inputProps={{ inputMode: 'numeric' }}
                  disabled={isSaving}
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ??
                    (organization
                      ? t('internal-csm:organizations_max_seats_help_used', { count: organization.userCount })
                      : t('internal-csm:organizations_max_seats_help'))
                  }
                />
              )}
            />
          </Stack>
        </Section>

        <Divider />
        <Section title={t('internal-csm:organizations_billing_details')}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              {renderText({ name: 'vatNumber', labelKey: 'internal-csm:organizations_vat_number', placeholder: '12345678' })}
            </Grid>
            {billingSelects.map(({ name: field, labelKey, options }) => (
              <Grid item xs={6} md={2} key={field}>
                <Controller
                  name={field}
                  control={control}
                  render={({ field: input }) => (
                    <TextField {...input} select size="small" fullWidth label={t(labelKey)} disabled={isSaving}>
                      <MenuItem value="">
                        <em>{t('internal-csm:organizations_not_set')}</em>
                      </MenuItem>
                      {withStoredOption(options, organization?.[field]).map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </Section>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Section title={t('internal-csm:organizations_billing_address')}>{billingAddress.map(renderText)}</Section>
          </Grid>
          <Grid item xs={12} md={6}>
            <Section title={t('internal-csm:organizations_billing_contact')}>
              {billingContact.map(({ name: field, labelKey, placeholder }) => (
                <Controller
                  key={field}
                  name={`billingContact.${field}`}
                  control={control}
                  render={({ field: input }) => (
                    <TextField {...input} size="small" fullWidth label={t(labelKey)} placeholder={placeholder} disabled={isSaving} />
                  )}
                />
              ))}
            </Section>
          </Grid>
        </Grid>
        <Divider />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Section title={t('internal-csm:organizations_company_address')}>{companyAddress.map(renderText)}</Section>
          </Grid>
          <Grid item xs={12} md={6}>
            <Section title={t('internal-csm:organizations_company_contact')}>{companyContact.map(renderText)}</Section>
          </Grid>
        </Grid>
      </Stack>
      <AddPackageDialog
        open={addPackageOpen}
        organizationName={displayName}
        catalog={sortedCatalog}
        addedIds={addedIds}
        onClose={() => setAddPackageOpen(false)}
        onAdd={handleAdd}
      />
      <ConfirmDialog open={!!removeConfirm} confirmDialogData={removeConfirm} onClose={handleRemoveConfirm} />
    </InternalFormDialog>
  )
}
